import React, { useState } from 'react';
import PropTypes from 'prop-types';
import s from './FormDropZone.module.scss';
import Dropzone from 'react-dropzone';
import { t } from '../../i18n';
import Icon from '../Icon';
import classNames from 'classnames';
import Button from '../Button/Button';

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

const FormDropZone = ({
    label = 'Upload files',
    files,
    setFiles,
    onRemoveFile,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [error, setError] = useState(null);

    const handleDrop = (acceptedFiles) => {
        setIsDragActive(false);
        setError(null);
        const transformedFiles = acceptedFiles.map((file) => ({
            originalFile: file,
            type: 'document',
            value: {
                title: getFileName(file.name),
                type: getFileFormat(file.name),
                file_url: file.relativePath,
                file_size: file.size,
            },
        }));
        setFiles((prevFiles) => [...prevFiles, ...transformedFiles]);
    };

    const handleRemoveFile = (fileIndex) => {
        const removedFile = files[fileIndex];
        setFiles((prevFiles) =>
            prevFiles.filter((_, index) => index !== fileIndex)
        );
        if (onRemoveFile) {
            onRemoveFile(removedFile);
        }
    };

    const getFileFormat = (name) => {
        const extension = name.split('.').pop();
        return extension ? extension.toUpperCase() : 'UNKNOWN';
    };

    const getFileName = (name) => {
        return name.split('.').slice(0, -1).join('.');
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        if (mb >= 1) {
            return `${mb.toFixed(1)} MB`;
        }
        const kb = bytes / 1024;
        return `${Math.round(kb)} KB`;
    };

    const handleDropRejected = (fileRejections) => {
        setIsDragActive(false);
        const errors = fileRejections.map((rejection) => {
            const code = rejection.errors[0]?.code;
            if (code === 'file-too-large') {
                return `"${rejection.file.name}" ${t('formDropZone.fileTooLarge')}`;
            }
            if (code === 'file-invalid-type') {
                return `"${rejection.file.name}" ${t('formDropZone.fileInvalidType')}`;
            }
            return `"${rejection.file.name}" ${t('formDropZone.fileRejected')}`;
        });
        setError(errors.join('. '));
    };

    const acceptedFileTypes = {
        'application/pdf': ['.pdf'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
        ],
        'text/csv': ['.csv'],
    };

    return (
        <div className={s.Root}>
            <div className={s.DropzoneWrapper}>
                <Dropzone
                    onDrop={handleDrop}
                    onDropRejected={handleDropRejected}
                    multiple
                    accept={acceptedFileTypes}
                    maxSize={MAX_FILE_SIZE}
                    onDragEnter={() => setIsDragActive(true)}
                    onDragLeave={() => setIsDragActive(false)}>
                    {({ getRootProps, getInputProps }) => (
                        <section
                            className={classNames(s.Dropzone, {
                                [s['Dropzone--IsDragActive']]: isDragActive,
                            })}>
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className={s.DropArea}>
                                    <div className={s.UploadIcon}>
                                        <Icon name="upload" size="large" />
                                    </div>
                                    <span className={s.Placeholder}>
                                        {t('formDropZone.dropItems')}
                                    </span>
                                    <span className={s.ChooseFiles}>
                                        {t('formDropZone.chooseFile')}
                                    </span>
                                </div>
                            </div>
                        </section>
                    )}
                </Dropzone>
                <label className={s.Label}>{label}</label>
                <div className={s.HelpTextWrapper}>
                    <span className={s.AcceptedFormats}>
                        {t('formDropZone.acceptedFormats')}
                    </span>
                    <span className={s.HelpText}>
                        {t('formDropZone.maxFileSize')}
                    </span>
                </div>
                {error && <div className={s.Error}>{error}</div>}
            </div>
            <div className={s.Preview}>
                {files?.length > 0 && (
                    <ul className={s.FileList}>
                        {files.map((file, index) => (
                            <li key={index} className={s.FileItem}>
                                <div className={s.FileWrapper}>
                                    <div className={s.IconWrapper}>
                                        <Icon name="file" />
                                    </div>
                                    <div className={s.FileNameWrapper}>
                                        <span className={s.FileName}>
                                            {file.value.title}
                                        </span>
                                        <div>
                                            <span className={s.FileExtension}>
                                                {file.value.type}
                                            </span>
                                            <span className={s.FileSize}>
                                                {formatFileSize(
                                                    file.value.file_size
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    modifier="Secondary"
                                    size="Large"
                                    onClick={() => handleRemoveFile(index)}
                                    title={t('formDropZone.delete')}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

FormDropZone.propTypes = {
    label: PropTypes.string,
    helptext: PropTypes.string,
    files: PropTypes.array.isRequired,
    setFiles: PropTypes.func.isRequired,
};

export default FormDropZone;
